import type { TrendingComponent, TrendingSource } from "../api/types";

const SOURCE_COLOR: Record<TrendingSource, string> = {
  apewisdom: "bg-sky-500",
  stocktwits: "bg-emerald-500",
  rss: "bg-amber-500",
};

const WEIGHTS: Record<TrendingSource, number> = {
  apewisdom: 0.45,
  stocktwits: 0.35,
  rss: 0.20,
};

export default function SourceBar({
  components,
}: {
  components: Partial<Record<TrendingSource, TrendingComponent>>;
}) {
  // Each source contributes `weight * z` to the aggregate. We display the
  // *positive* contributions as a stacked bar — negative z (decay) shows as
  // a faint backdrop instead of negative width.
  const segments = (Object.entries(components) as [TrendingSource, TrendingComponent][])
    .map(([source, c]) => ({
      source,
      contrib: Math.max(0, WEIGHTS[source] * c.z),
      raw: c.raw,
      z: c.z,
    }));
  const total = segments.reduce((s, x) => s + x.contrib, 0) || 1;

  return (
    <div className="space-y-1">
      <div className="flex h-1 w-full overflow-hidden rounded-full bg-slate-800">
        {segments.map(({ source, contrib }) => (
          <div
            key={source}
            className={`${SOURCE_COLOR[source]} transition-[width]`}
            style={{ width: `${(contrib / total) * 100}%` }}
            title={`${source}: ${contrib.toFixed(2)}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] tabular-nums text-slate-500">
        {segments.map(({ source, raw, z }) => (
          <span key={source} className="inline-flex items-center gap-1">
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${SOURCE_COLOR[source]}`} />
            <span className="text-slate-400">{source}</span>
            <span>z={z.toFixed(1)}</span>
            <span className="text-slate-600">({raw.toFixed(0)})</span>
          </span>
        ))}
      </div>
    </div>
  );
}
